// NIP: walidacja i dane firmy pobierane z rejestrów (GUS REGON, biała lista podatników VAT) — docs/10 §17.

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

/** Same cyfry NIP (bez spacji, kresek i prefiksu „PL”). */
export function normalizeNip(value: string): string {
  return value.replace(/^\s*PL/i, '').replace(/\D/g, '');
}

/** Poprawny NIP: 10 cyfr i zgodna suma kontrolna (wagi 6,5,7,2,3,4,5,6,7; reszta 10 jest zawsze błędna). */
export function isValidNip(value: string): boolean {
  const d = normalizeNip(value);
  if (!/^\d{10}$/.test(d) || d === '0000000000') return false;
  const sum = NIP_WEIGHTS.reduce((s, w, i) => s + w * Number(d[i]), 0);
  const check = sum % 11;
  return check !== 10 && check === Number(d[9]);
}

/** Status z białej listy MF; „brak” — NIP nie występuje w wykazie podatników VAT. */
export type VatStatus = 'Czynny' | 'Zwolniony' | 'Niezarejestrowany' | 'brak';

export const VAT_STATUS_LABELS: Record<VatStatus, string> = {
  Czynny: 'czynny podatnik VAT',
  Zwolniony: 'zwolniony z VAT',
  Niezarejestrowany: 'niezarejestrowany jako podatnik VAT',
  brak: 'brak w wykazie podatników VAT',
};

export interface VatCheckDto {
  status: VatStatus;
  /** moment sprawdzenia (ISO) */
  checkedAt: string;
  /** identyfikator zapytania z białej listy — dowód sprawdzenia */
  requestId: string;
}

export interface NipLookupDto {
  nip: string;
  /** skąd pochodzą nazwa i adres; null — żaden rejestr nie zna NIP (albo nie odpowiedział) */
  source: 'regon' | 'biala_lista' | null;
  company: {
    name: string;
    street: string;
    postalCode: string;
    city: string;
    regon: string;
    krs: string;
    /** data zakończenia działalności wg REGON (RRRR-MM-DD); null — działa albo brak danych z REGON */
    endedAt: string | null;
    /** status NIP w REGON (np. „Uchylony”, „Unieważniony”); null — w porządku */
    nipStatus: string | null;
  } | null;
  /** null — sprawdzenie na białej liście nie powiodło się (vatError) */
  vat: VatCheckDto | null;
  vatError: string | null;
  /** ok — znaleziono; brak — nie ma w REGON; wylaczone — brak klucza GUS_REGON_KEY; blad — usługa nie odpowiedziała */
  regon: 'ok' | 'brak' | 'wylaczone' | 'blad';
  regonError: string | null;
}

/** Ostrzeżenie do pokazania przy firmie (null — wszystko w porządku). Nie blokuje zapisu. */
export function nipWarning(r: Pick<NipLookupDto, 'vat' | 'company'>): string | null {
  const parts: string[] = [];
  if (r.vat && r.vat.status !== 'Czynny') parts.push(`Biała lista: ${VAT_STATUS_LABELS[r.vat.status]}`);
  if (r.company?.endedAt) parts.push(`REGON: działalność zakończona ${r.company.endedAt.split('-').reverse().join('.')}`);
  if (r.company?.nipStatus) parts.push(`REGON: NIP ${r.company.nipStatus.toLowerCase()}`);
  return parts.length ? parts.join(' · ') : null;
}
